import { gql, useMutation, useQuery } from '@apollo/client'
import { PropsWithChildren, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { SERVICE_ACCESS_PIN_STORAGE_KEY } from 'shared/constants/serviceAccess'
import Button from 'shared/ui/Button_2'
import NumbersInput, {
  NumbersInputStatuses
} from 'shared/ui/NumbersInput'
import Spinner from 'shared/ui/Spinner'
import Text from 'shared/ui/Text'
import { ButtonIntents } from 'shared/ui/Button'

const SERVICE_ACCESS_STATUS = gql`
  query ServiceAccessStatus {
    serviceAccessStatus {
      required
      granted
      lockedUntil
    }
  }
`

const VERIFY_SERVICE_ACCESS_PIN = gql`
  mutation VerifyServiceAccessPin($pin: String!) {
    verifyServiceAccessPin(pin: $pin) {
      granted
      lockedUntil
    }
  }
`

type ServiceAccessStatusData = {
  serviceAccessStatus: {
    required: boolean
    granted: boolean
    lockedUntil: number | null
  }
}

type VerifyServiceAccessPinData = {
  verifyServiceAccessPin: {
    granted: boolean
    lockedUntil: number | null
  }
}

const Page = styled.main`
  min-height: 100vh;
  padding: 24px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.color.base.c1};
`

const Card = styled.section`
  width: 100%;
  max-width: 560px;
  padding: 48px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 24px;
  background: ${({ theme }) => theme.color.base.c0};
  box-shadow: 0 12px 40px rgba(33, 37, 41, 0.12);

  @media (max-width: 480px) {
    padding: 40px 16px;
  }
`

const PinWrapper = styled.div`
  max-width: 100%;
  margin: 32px 0 16px;
  overflow-x: auto;
`

const ErrorText = styled.div`
  min-height: 24px;
  margin-bottom: 16px;
`

const ServiceAccessGate = ({ children }: PropsWithChildren) => {
  const [pin, setPin] = useState('')
  const [status, setStatus] = useState(NumbersInputStatuses.default)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [manualLockedUntil, setManualLockedUntil] = useState<
    number | null
  >(null)
  const [now, setNow] = useState(Date.now())
  const { formatMessage } = useIntl()
  const [verifyServiceAccessPin] =
    useMutation<VerifyServiceAccessPinData>(
      VERIFY_SERVICE_ACCESS_PIN
    )
  const { data, loading, refetch } = useQuery<ServiceAccessStatusData>(
    SERVICE_ACCESS_STATUS,
    {
      fetchPolicy: 'network-only',
      pollInterval: 60_000
    }
  )

  const access = data?.serviceAccessStatus
  const lockedUntil =
    manualLockedUntil ?? access?.lockedUntil ?? null
  const isLocked = !!lockedUntil && lockedUntil > now
  const remainingMinutes = lockedUntil
    ? Math.max(1, Math.ceil((lockedUntil - now) / 60_000))
    : 0

  useEffect(() => {
    if (!lockedUntil || lockedUntil <= Date.now()) {
      return
    }

    const interval = window.setInterval(() => {
      const currentTime = Date.now()
      setNow(currentTime)

      if (currentTime >= lockedUntil) {
        window.clearInterval(interval)
      }
    }, 1_000)

    return () => window.clearInterval(interval)
  }, [lockedUntil])

  if (loading && !access) {
    return (
      <Page>
        <Spinner />
      </Page>
    )
  }

  if (access && (!access.required || access.granted)) {
    return <>{children}</>
  }

  const isPinComplete = pin.length === 6 && !pin.includes('-')

  const verifyPin = async () => {
    if (!isPinComplete || isSubmitting || isLocked) {
      return
    }

    setIsSubmitting(true)
    setStatus(NumbersInputStatuses.default)

    try {
      const result = await verifyServiceAccessPin({
        variables: { pin }
      })
      const verification = result.data?.verifyServiceAccessPin

      if (verification?.granted) {
        localStorage.setItem(SERVICE_ACCESS_PIN_STORAGE_KEY, pin)
        setManualLockedUntil(null)
        setStatus(NumbersInputStatuses.success)
        await refetch()
        return
      }

      localStorage.removeItem(SERVICE_ACCESS_PIN_STORAGE_KEY)
      setManualLockedUntil(verification?.lockedUntil ?? null)
      setStatus(NumbersInputStatuses.error)
    } catch {
      localStorage.removeItem(SERVICE_ACCESS_PIN_STORAGE_KEY)
      setStatus(NumbersInputStatuses.error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Page>
      <Card>
        <Text
          tag="h1"
          id="ServiceAccess.title"
          size={32}
          lineHeight="40px"
          weight={700}
          align="center"
          marginBottom={16}
        />
        <Text
          tag="p"
          id="ServiceAccess.description"
          size={16}
          lineHeight="24px"
          align="center"
          maxWidth={440}
        />
        <PinWrapper>
          <NumbersInput
            count={6}
            gap={6}
            status={status}
            disabled={isSubmitting || isLocked}
            onChange={value => {
              setPin(value)
              setStatus(NumbersInputStatuses.default)
            }}
          />
        </PinWrapper>
        <ErrorText>
          {isLocked ? (
            <Text
              text={formatMessage(
                { id: 'ServiceAccess.locked' },
                { minutes: remainingMinutes }
              )}
              color="#BE123C"
              align="center"
            />
          ) : status === NumbersInputStatuses.error ? (
            <Text
              id="ServiceAccess.invalidPin"
              color="#BE123C"
              align="center"
            />
          ) : null}
        </ErrorText>
        <Button
          intent='primary-brand-02'
          textId="ServiceAccess.submit"
          minWidth={180}
          loading={isSubmitting}
          state={
            isPinComplete && !isLocked ? 'default' : 'disabled'
          }
          isDisabled={!isPinComplete || isLocked}
          onPress={verifyPin}
        />
      </Card>
    </Page>
  )
}

export default ServiceAccessGate
